import { ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';

const ZOHO_ORG = process.env.REACT_APP_ZOHO_BOOKS_ORG_ID;
const BASE = ZOHO_ORG ? `https://books.zoho.com/app/${ZOHO_ORG}#` : null;

// In-memory cache so we hit the backend at most once per enrollment per session
const _crmLeadUrlCache = new Map();

export function ZohoCustomerLink({ enrollmentNo, zohoCustomerId, className = '' }) {
  if (!BASE || !zohoCustomerId) return <span className={className}>{enrollmentNo || '-'}</span>;
  return (
    <a
      href={`${BASE}/contacts/${zohoCustomerId}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-0.5 text-violet-600 hover:text-violet-800 hover:underline font-medium ${className}`}
      onClick={e => e.stopPropagation()}
      data-testid={`zoho-customer-link-${enrollmentNo}`}
    >
      {enrollmentNo || '-'}
      <ExternalLink className="w-2.5 h-2.5 opacity-40" />
    </a>
  );
}

export function ZohoInvoiceLink({ invoiceNumber, invoiceId, className = '' }) {
  if (!BASE || !invoiceId) return <span className={className}>{invoiceNumber || '-'}</span>;
  return (
    <a
      href={`${BASE}/invoices/${invoiceId}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 hover:underline font-medium ${className}`}
      onClick={e => e.stopPropagation()}
      data-testid={`zoho-invoice-link-${invoiceId}`}
    >
      {invoiceNumber || '-'}
      <ExternalLink className="w-2.5 h-2.5 opacity-40" />
    </a>
  );
}

/**
 * EnrollmentNoCrmLink — renders the enrollment number itself as a clickable link
 * that opens the matching Zoho CRM Lead. Used inside data tables where we want a
 * compact inline link (NOT the orange button). Click → resolves lead URL via
 * cached endpoint → opens new tab. Falls back to plain text when enrollment is
 * known to have no Lead (parent passes hasLead=false).
 */
export function EnrollmentNoCrmLink({ enrollmentNo, hasLead = true, className = '' }) {
  const [loading, setLoading] = useState(false);

  if (!enrollmentNo) return <span className={className}>-</span>;

  // Known-missing — render as plain text so user knows clicking won't help
  if (hasLead === false) {
    return <span className={`text-slate-500 ${className}`} data-testid={`enrollment-no-plain-${enrollmentNo}`}>{enrollmentNo}</span>;
  }

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (_crmLeadUrlCache.has(enrollmentNo)) {
      const cached = _crmLeadUrlCache.get(enrollmentNo);
      if (cached) window.open(cached, '_blank', 'noopener,noreferrer');
      else toast.error('No CRM Lead found for this enrollment');
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/training/crm/lead-url/${enrollmentNo}`);
      const url = r.data?.url;
      _crmLeadUrlCache.set(enrollmentNo, url);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      _crmLeadUrlCache.set(enrollmentNo, null);
      // Notify the page to refresh local crmLeadLinks map + KPI count
      try { window.dispatchEvent(new CustomEvent('crm-lead-missing', { detail: { enrollmentNo: String(enrollmentNo) } })); } catch (_) {}
      toast.error(err.response?.status === 404
        ? `No CRM Lead found for enrollment ${enrollmentNo}`
        : 'Failed to look up CRM Lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      title={`Open Zoho CRM Lead for enrollment ${enrollmentNo}`}
      className={`inline-flex items-center gap-0.5 text-orange-600 hover:text-orange-800 hover:underline font-medium disabled:opacity-50 ${className}`}
      data-testid={`enrollment-no-crm-link-${enrollmentNo}`}
    >
      {enrollmentNo}
      {loading
        ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
        : <ExternalLink className="w-2.5 h-2.5 opacity-40" />}
    </button>
  );
}

/**
 * ZohoCrmBatchLink — opens the Zoho CRM Batch (CustomModule22) page for a batch.
 * Pass either crmLink (full URL from batch.crm_link) or zohoBatchId.
 */
export function ZohoCrmBatchLink({ crmLink, zohoBatchId, label = 'Open Batch in CRM', className = '', compact = false }) {
  const orgId = process.env.REACT_APP_ZOHO_CRM_ORG_ID || '690252559';
  const url = crmLink || (zohoBatchId ? `https://crm.zoho.com/crm/org${orgId}/tab/CustomModule22/${zohoBatchId}` : null);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      title="Open this batch in Zoho CRM"
      className={`inline-flex items-center gap-1 ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 font-medium transition-colors ${className}`}
      data-testid={`zoho-crm-batch-link-${zohoBatchId || 'link'}`}
    >
      <ExternalLink className="w-3 h-3" />
      {label}
    </a>
  );
}

/**
 * ZohoCrmLeadLink — opens the Zoho CRM Lead matching this enrollment_no.
 * Shows "Open in CRM" label + external icon. Reads from local DB cache first
 * (populated during CRM sync); falls back to live search.
 */
export function ZohoCrmLeadLink({ enrollmentNo, label = 'Open in CRM', className = '', compact = false }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enrollmentNo) return;

    if (_crmLeadUrlCache.has(enrollmentNo)) {
      const cached = _crmLeadUrlCache.get(enrollmentNo);
      if (cached) window.open(cached, '_blank', 'noopener,noreferrer');
      else toast.error('No CRM Lead found for this enrollment');
      return;
    }

    setLoading(true);
    try {
      const r = await api.get(`/training/crm/lead-url/${enrollmentNo}`);
      const url = r.data?.url;
      _crmLeadUrlCache.set(enrollmentNo, url);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      _crmLeadUrlCache.set(enrollmentNo, null);
      try { window.dispatchEvent(new CustomEvent('crm-lead-missing', { detail: { enrollmentNo: String(enrollmentNo) } })); } catch (_) {}
      const msg = err.response?.status === 404
        ? `No CRM Lead found for enrollment ${enrollmentNo}`
        : 'Failed to look up CRM Lead';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!enrollmentNo) return <span className={className}>-</span>;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      title={`Open Zoho CRM Lead for enrollment ${enrollmentNo}`}
      className={`inline-flex items-center gap-1 ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded-sm border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 hover:border-orange-300 font-medium transition-colors disabled:opacity-50 ${className}`}
      data-testid={`zoho-crm-lead-link-${enrollmentNo}`}
    >
      {loading
        ? <Loader2 className="w-3 h-3 animate-spin" />
        : <ExternalLink className="w-3 h-3" />}
      {label}
    </button>
  );
}

/**
 * ZohoBooksCustomerButton — opens the Zoho Books customer page for this student.
 * Same visual style as `ZohoCrmLeadLink` but in Books blue. Use in drill-downs
 * where we want parallel "Open in CRM" + "Open in Books" buttons.
 */
export function ZohoBooksCustomerButton({ zohoCustomerId, label = 'Open in Books', className = '', compact = false }) {
  if (!BASE || !zohoCustomerId) return null;
  return (
    <a
      href={`${BASE}/contacts/${zohoCustomerId}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      title="Open this customer in Zoho Books"
      className={`inline-flex items-center gap-1 ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} rounded-sm border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 font-medium transition-colors ${className}`}
      data-testid={`zoho-books-customer-btn-${zohoCustomerId}`}
    >
      <ExternalLink className="w-3 h-3" />
      {label}
    </a>
  );
}

/**
 * MissingCrmLeadBadge — small amber chip surfaced on rows whose enrollment_no
 * has NO matching CRM Lead (i.e. salesperson never set Lead.Enrollment_No).
 * Renders only if `missing` is true.
 */
export function MissingCrmLeadBadge({ missing, compact = false, className = '' }) {
  if (!missing) return null;
  return (
    <span
      title="Enrollment number not set on any CRM Lead — please ask the salesperson to update it"
      className={`inline-flex items-center gap-0.5 ${compact ? 'px-1 py-0 text-[8px]' : 'px-1.5 py-0.5 text-[9px]'} rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium ${className}`}
      data-testid="missing-crm-lead-badge"
    >
      <AlertCircle className={`${compact ? 'w-2 h-2' : 'w-2.5 h-2.5'}`} />
      No CRM
    </span>
  );
}
